import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { IFCLoader } from "web-ifc-three/IFCLoader";
import {
  IFCWALL, IFCWALLSTANDARDCASE, IFCCURTAINWALL,
  IFCSLAB,
  IFCDOOR,
  IFCWINDOW,
  IFCROOF,
  IFCCOLUMN,
  IFCBEAM, IFCMEMBER, IFCPLATE,
  IFCSTAIR, IFCSTAIRFLIGHT,
  IFCFURNISHINGELEMENT,
  IFCRAILING,
  IFCLIGHTFIXTURE,
  IFCSPACE,
  IFCBUILDINGELEMENTPROXY,
  IFCCOVERING,
  IFCPILE,
  IFCFOOTING,
  IFCREINFORCINGBAR,
  IFCREINFORCINGMESH,
  IFCTENDON,
  IFCTENDONANCHOR,
  IFCRAMP, IFCRAMPFLIGHT,
  IFCELEMENTASSEMBLY,
  IFCDISCRETEACCESSORY,
} from "web-ifc";

import { Tooltip } from "@material-ui/core";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Straighten as StraightenIcon,
  SquareFoot as SquareFootIcon,
  GetApp as GetAppIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@material-ui/icons";
import LayersIcon from "@material-ui/icons/Layers";
import PlaceIcon from "@material-ui/icons/Place";
import StraightenIcon2 from "@material-ui/icons/Straighten";
import LoginPopup from "components/login-popup";

import "./BimViewer.css";

function disposeBimSceneHelpers(scene, helpersRef) {
  if (!scene || !helpersRef?.current) return;
  const h = helpersRef.current;
  ["grid", "axes"].forEach((key) => {
    const obj = h[key];
    if (!obj) return;
    try { scene.remove(obj); } catch {}
    try {
      obj.geometry?.dispose?.();
      const m = obj.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose?.());
      else m?.dispose?.();
    } catch {}
    h[key] = null;
  });
}

const IFC_CATEGORIES = [
  { name: "Paredes",      types: [IFCWALL, IFCWALLSTANDARDCASE, IFCCURTAINWALL] },
  { name: "Lajes",        types: [IFCSLAB] },
  { name: "Portas",       types: [IFCDOOR] },
  { name: "Janelas",      types: [IFCWINDOW] },
  { name: "Cobertura",    types: [IFCROOF] },
  { name: "Colunas",      types: [IFCCOLUMN] },
  { name: "Vigas",        types: [IFCBEAM, IFCMEMBER, IFCPLATE] },
  { name: "Escadas",      types: [IFCSTAIR, IFCSTAIRFLIGHT] },
  { name: "Gradis",       types: [IFCRAILING] },
  { name: "Iluminação",   types: [IFCLIGHTFIXTURE] },
  { name: "Mobiliário",   types: [IFCFURNISHINGELEMENT] },
  { name: "Espaços",      types: [IFCSPACE] },
];

const IFC_CATCHALL_TYPES = [
  IFCBUILDINGELEMENTPROXY,
  IFCCOVERING,
  IFCPILE,
  IFCFOOTING,
  IFCREINFORCINGBAR,
  IFCREINFORCINGMESH,
  IFCTENDON,
  IFCTENDONANCHOR,
  IFCRAMP, IFCRAMPFLIGHT,
  IFCELEMENTASSEMBLY,
  IFCDISCRETEACCESSORY,
];

function formatIfcValue(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "object" && "value" in val) return val.value;
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return val;
  return null;
}

function flattenIfcProps(props) {
  if (!props || typeof props !== "object") return {};
  const SKIP = new Set(["expressID", "type"]);
  const out = {};
  for (const [key, val] of Object.entries(props)) {
    if (SKIP.has(key)) continue;
    const formatted = formatIfcValue(val);
    if (formatted !== null && formatted !== undefined && formatted !== "") {
      out[key] = formatted;
    }
  }
  return out;
}

function safeJsonStringify(value) {
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

function getSharedProxyUrl({ baseUrl, token, path }) {
  if (!path) return null;
  return `${baseUrl}/projects/shared/${token}/blob?path=${encodeURIComponent(path)}`;
}

const SharedBimViewer = () => {
  const location   = useLocation();
  const navigate   = useNavigate();
  const shareToken = new URLSearchParams(location.search).get("token");

  const handleCloseShared = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const baseUrl = useMemo(() => {
    const candidate = process.env.REACT_APP_API_URL || api.defaults.baseURL || "";
    return String(candidate).replace(/\/$/, "");
  }, []);

  const [loading,    setLoading]    = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("Carregando…");
  const [error,      setError]      = useState(null);

  const [assets,      setAssets]      = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [layerToggles, setLayerToggles] = useState([]);
  const layerTogglesRef = useRef([]);
  useEffect(() => { layerTogglesRef.current = layerToggles; }, [layerToggles]);

  const [selectedProps, setSelectedProps] = useState(null);

  const [measureMode,       setMeasureMode]       = useState(null);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurementValue,  setMeasurementValue]  = useState(null);

  const canvasMountRef = useRef(null);
  const rendererRef    = useRef(null);
  const sceneRef       = useRef(null);
  const cameraRef      = useRef(null);
  const controlsRef    = useRef(null);

  const ifcLoaderRef  = useRef(null);
  const ifcModelIdRef = useRef(null);
  const modelRootRef  = useRef(null);

  const selectableMeshesRef = useRef([]);
  const raycasterRef        = useRef(new THREE.Raycaster());
  const highlightStateRef   = useRef({ mesh: null, originalEmissive: null, originalColor: null });
  const measurementObjectsRef = useRef({ markers: [], lines: [], previewLine: null });
  const bimHelpersRef = useRef({ grid: null, axes: null });
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(true);

  const [hoverTip, setHoverTip] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerRootRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const pendingHoverRef = useRef(null);

  // ── Dispose ─────────────────────────────────────────────────────────────────
  const disposeCurrentIFC = useCallback(() => {
    const scene   = sceneRef.current;
    const loader  = ifcLoaderRef.current;
    const modelID = ifcModelIdRef.current;
    disposeBimSceneHelpers(scene, bimHelpersRef);
    for (const layer of layerTogglesRef.current) {
      try { if (layer.subsetMesh && scene) scene.remove(layer.subsetMesh); } catch {}
    }
    if (modelRootRef.current && scene) { try { scene.remove(modelRootRef.current); } catch {} }
    if (loader && modelID !== null)    { try { loader.ifcManager.removeIfcModel(modelID); } catch {} }
    ifcModelIdRef.current = null;
    modelRootRef.current  = null;
    selectableMeshesRef.current = [];
    setLayerToggles([]);
    setSelectedProps(null);
  }, []);

  const removePreviewLine = useCallback(() => {
    const scene = sceneRef.current;
    const pl = measurementObjectsRef.current.previewLine;
    if (!scene || !pl) return;
    scene.remove(pl);
    pl.geometry?.dispose?.();
    pl.material?.dispose?.();
    measurementObjectsRef.current.previewLine = null;
  }, []);

  const clearMeasurements = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const { markers, lines } = measurementObjectsRef.current;
    removePreviewLine();
    for (const m of markers) { scene.remove(m); m.geometry?.dispose?.(); m.material?.dispose?.(); }
    for (const l of lines)   { scene.remove(l); l.geometry?.dispose?.(); l.material?.dispose?.(); }
    measurementObjectsRef.current = { markers: [], lines: [], previewLine: null };
  }, [removePreviewLine]);

  const setOrUpdatePreviewLine = useCallback((p1, p2) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const prev = measurementObjectsRef.current.previewLine;
    if (prev) {
      scene.remove(prev);
      prev.geometry?.dispose?.();
      prev.material?.dispose?.();
    }
    const geom = new THREE.BufferGeometry().setFromPoints([p1, p2]);
    const mat  = new THREE.LineDashedMaterial({
      color: 0x38bdf8,
      dashSize: 0.25,
      gapSize: 0.15,
      transparent: true,
      opacity: 0.85,
    });
    const line = new THREE.Line(geom, mat);
    line.computeLineDistances();
    scene.add(line);
    measurementObjectsRef.current.previewLine = line;
  }, []);

  const addMarker = useCallback(point => {
    const scene = sceneRef.current;
    if (!scene) return;
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 24, 24),
      new THREE.MeshStandardMaterial({ color: 0x38bdf8, emissive: 0x082f49, emissiveIntensity: 0.75 })
    );
    mesh.position.copy(point);
    scene.add(mesh);
    measurementObjectsRef.current.markers.push(mesh);
  }, []);

  const addOrUpdateDistanceLine = useCallback((p1, p2) => {
    const scene = sceneRef.current;
    if (!scene) return;
    for (const l of measurementObjectsRef.current.lines) scene.remove(l);
    measurementObjectsRef.current.lines = [];
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([p1, p2]),
      new THREE.LineBasicMaterial({ color: 0x38bdf8 })
    );
    scene.add(line);
    measurementObjectsRef.current.lines.push(line);
  }, []);

  const addOrUpdateAreaPolygon = useCallback(pts => {
    const scene = sceneRef.current;
    if (!scene || pts.length < 3) return;
    for (const l of measurementObjectsRef.current.lines) scene.remove(l);
    measurementObjectsRef.current.lines = [];
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([...pts, pts[0]]),
      new THREE.LineBasicMaterial({ color: 0x38bdf8 })
    );
    scene.add(line);
    measurementObjectsRef.current.lines.push(line);
  }, []);

  const computePolygonArea = useCallback(pts => {
    if (!Array.isArray(pts) || pts.length < 3) return null;
    let area = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const ab = new THREE.Vector3().subVectors(pts[i],     pts[0]);
      const ac = new THREE.Vector3().subVectors(pts[i + 1], pts[0]);
      area += new THREE.Vector3().crossVectors(ab, ac).length() * 0.5;
    }
    return area;
  }, []);

  const highlightMesh = useCallback(mesh => {
    const hs = highlightStateRef.current;
    if (hs.mesh) {
      try {
        const mat = Array.isArray(hs.mesh.material) ? hs.mesh.material[0] : hs.mesh.material;
        if (hs.originalEmissive && mat?.emissive) mat.emissive.copy(hs.originalEmissive);
        if (hs.originalColor   && mat?.color)    mat.color.copy(hs.originalColor);
      } catch {}
    }
    highlightStateRef.current = { mesh: null, originalEmissive: null, originalColor: null };
    if (!mesh) return;
    try {
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (mat?.emissive) {
        highlightStateRef.current = { mesh, originalEmissive: mat.emissive.clone(), originalColor: null };
        mat.emissive.setHex(0xdc2626);
      } else if (mat?.color) {
        highlightStateRef.current = { mesh, originalEmissive: null, originalColor: mat.color.clone() };
        mat.color.setHex(0xdc2626);
      }
    } catch {}
  }, []);

  const clearHighlightAndSelection = useCallback(() => {
    highlightMesh(null);
    setSelectedProps(null);
  }, [highlightMesh]);

  const getPointerNDC = useCallback(e => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width)  * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    );
  }, []);

  const computeSceneBoundingBox = useCallback(() => {
    const model = modelRootRef.current;
    const layers = layerTogglesRef.current;
    const box = new THREE.Box3();
    let has = false;
    for (const l of layers) {
      if (l.subsetMesh) {
        box.expandByObject(l.subsetMesh);
        has = true;
      }
    }
    if (!has && model) {
      box.setFromObject(model);
      has = true;
    }
    if (!has) return null;
    return box;
  }, []);

  const frameCameraFit = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.5;
    controls.target.copy(c2);
    cam.position.set(c2.x + dist, c2.y + dist * 0.6, c2.z + dist);
    cam.up.set(0, 1, 0);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewTop = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x, c2.y + dist, c2.z);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewIso = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.5;
    const dir = new THREE.Vector3(1, 1, 1).normalize();
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.copy(c2).addScaledVector(dir, dist);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewFront = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x, c2.y, c2.z + dist);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewRight = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x + dist, c2.y, c2.z);
    controls.update();
  }, [computeSceneBoundingBox]);

  const exportMeasurements = useCallback(() => {
    if (!measureMode || measurementPoints.length === 0) return;
    const payload = {
      exportedAt: new Date().toISOString(),
      measureMode,
      unit: measureMode === "distance" ? "m" : "m²",
      value: measurementValue,
      points: measurementPoints.map(p => [p.x, p.y, p.z]),
    };
    const blob = new Blob([safeJsonStringify(payload)], { type: "application/json;charset=utf-8" });
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `bim-medicao-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [measureMode, measurementPoints, measurementValue]);

  const toggleFullscreen = useCallback(() => {
    const el = viewerRootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const syncBimHelpers = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    disposeBimSceneHelpers(scene, bimHelpersRef);
    if (!showGrid && !showAxes) return;
    const box = computeSceneBoundingBox();
    if (!box || box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const extent = Math.max(size.x, size.y, size.z);
    const h = bimHelpersRef.current;
    if (showGrid) {
      const gridSize = Math.max(size.x, size.z, 1) * 1.25;
      const div = Math.min(48, Math.max(12, Math.round(gridSize / 2)));
      const grid = new THREE.GridHelper(gridSize, div, 0x64748b, 0x334155);
      grid.position.set(center.x, box.min.y - 0.002, center.z);
      scene.add(grid);
      h.grid = grid;
    }
    if (showAxes) {
      const axLen = Math.max(0.5, extent * 0.22);
      const axes = new THREE.AxesHelper(axLen);
      axes.position.copy(center);
      scene.add(axes);
      h.axes = axes;
    }
  }, [showGrid, showAxes, computeSceneBoundingBox]);

  // ── Renderer init ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasMountRef.current) return;
    const w = canvasMountRef.current.clientWidth  || window.innerWidth;
    const h = canvasMountRef.current.clientHeight || window.innerHeight;

    const scene    = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1220);
    const camera   = new THREE.PerspectiveCamera(60, w / h, 0.01, 100000);
    camera.position.set(10, 10, 10);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    canvasMountRef.current.innerHTML = "";
    canvasMountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x223344, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(5, 10, 7);
    scene.add(dir);

    sceneRef.current    = scene;
    cameraRef.current   = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;

    const onResize = () => {
      if (!canvasMountRef.current) return;
      const nw = canvasMountRef.current.clientWidth  || window.innerWidth;
      const nh = canvasMountRef.current.clientHeight || window.innerHeight;
      renderer.setSize(nw, nh);
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    let animId = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(animId);
      try { disposeBimSceneHelpers(scene, bimHelpersRef); } catch {}
      try { controls.dispose(); renderer.dispose(); } catch {}
      sceneRef.current = cameraRef.current = rendererRef.current = controlsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onFs = () => {
      setIsFullscreen(!!document.fullscreenElement);
      const mount = canvasMountRef.current;
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      if (!mount || !renderer || !camera) return;
      const nw = mount.clientWidth || window.innerWidth;
      const nh = mount.clientHeight || window.innerHeight;
      renderer.setSize(nw, nh);
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ── Click ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;

    const onPointerDown = async e => {
      const scene  = sceneRef.current;
      const camera = cameraRef.current;
      if (!scene || !camera) return;

      const point = getPointerNDC(e);
      if (!point) return;

      raycasterRef.current.setFromCamera(point, camera);
      const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
      if (!hits.length) return;

      const hit      = hits[0];
      const hitPoint = hit.point.clone();

      if (measureMode === "distance" || measureMode === "area") {
        if (measureMode === "distance" && measurementPoints.length >= 2) {
          clearMeasurements();
          setMeasurementPoints([hitPoint]);
          setMeasurementValue(null);
          addMarker(hitPoint);
          return;
        }
        const next = [...measurementPoints, hitPoint];
        setMeasurementPoints(next);
        setMeasurementValue(null);
        addMarker(hitPoint);
        removePreviewLine();
        if (measureMode === "distance" && next.length === 2) {
          addOrUpdateDistanceLine(next[0], next[1]);
          setMeasurementValue(next[0].distanceTo(next[1]));
        }
        if (measureMode === "area" && next.length >= 3) {
          addOrUpdateAreaPolygon(next);
          setMeasurementValue(computePolygonArea(next));
        }
        return;
      }

      const loader  = ifcLoaderRef.current;
      const modelID = ifcModelIdRef.current;
      if (!loader || modelID === null || hit.faceIndex === undefined) return;

      try {
        const expressID = loader.ifcManager.getExpressId(hit.object.geometry, hit.faceIndex);
        if (expressID === undefined || expressID === null) return;
        highlightMesh(hit.object);
        const props = await loader.ifcManager.getItemProperties(modelID, expressID, true);
        setSelectedProps(flattenIfcProps(props));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[SharedBimViewer] erro ao obter propriedades:", err);
      }
    };

    const onPointerMove = e => {
      const cam = cameraRef.current;
      if (!cam) return;

      if (measureMode === "distance" || measureMode === "area") {
        setHoverTip(null);
        if (measureMode === "distance" && measurementPoints.length !== 1) {
          if (measurementPoints.length === 0) removePreviewLine();
          return;
        }
        if (measureMode === "area" && measurementPoints.length === 0) {
          removePreviewLine();
          return;
        }
        const ndc = getPointerNDC(e);
        if (!ndc) return;
        raycasterRef.current.setFromCamera(ndc, cam);
        const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
        if (!hits.length) {
          removePreviewLine();
          return;
        }
        const from = measurementPoints[measurementPoints.length - 1];
        setOrUpdatePreviewLine(from, hits[0].point);
        return;
      }

      removePreviewLine();
      const ndcHover = getPointerNDC(e);
      if (!ndcHover) {
        setHoverTip(null);
        return;
      }
      pendingHoverRef.current = { clientX: e.clientX, clientY: e.clientY, ndc: ndcHover };
      if (hoverFrameRef.current !== null) return;
      hoverFrameRef.current = requestAnimationFrame(() => {
        hoverFrameRef.current = null;
        const p = pendingHoverRef.current;
        if (!p) return;
        const cam2 = cameraRef.current;
        if (!cam2) return;
        raycasterRef.current.setFromCamera(p.ndc, cam2);
        const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
        if (!hits.length) {
          setHoverTip(null);
          return;
        }
        const hit = hits[0];
        const loader = ifcLoaderRef.current;
        const modelID = ifcModelIdRef.current;
        if (!loader || modelID === null || hit.faceIndex === undefined) {
          setHoverTip(null);
          return;
        }
        try {
          const expressID = loader.ifcManager.getExpressId(hit.object.geometry, hit.faceIndex);
          if (expressID === undefined || expressID === null) {
            setHoverTip(null);
            return;
          }
          let typeStr = "";
          try {
            const t = loader.ifcManager.getIfcType(modelID, expressID);
            if (t) typeStr = String(t);
          } catch { /* ignore */ }
          const text = typeStr ? `#${expressID} · ${typeStr}` : `ExpressID ${expressID}`;
          setHoverTip({ x: p.clientX, y: p.clientY, text });
        } catch {
          setHoverTip(null);
        }
      });
    };

    const onPointerLeave = () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
      }
      pendingHoverRef.current = null;
      setHoverTip(null);
      removePreviewLine();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerleave", onPointerLeave);
    return () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
      }
      pendingHoverRef.current = null;
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [
    getPointerNDC, addMarker, addOrUpdateDistanceLine, addOrUpdateAreaPolygon,
    computePolygonArea, highlightMesh, measureMode, measurementPoints,
    clearMeasurements, removePreviewLine, setOrUpdatePreviewLine,
  ]);

  // ── Carrega IFC compartilhado ────────────────────────────────────────────────
  useEffect(() => {
    if (!shareToken) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadingMsg("Carregando…");
      setError(null);

      try {
        disposeCurrentIFC();
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);

        const response  = await api.get(`/projects/shared/${shareToken}`);
        const rawFiles  = response.data?.files || response.data || [];
        const list      = Array.isArray(rawFiles) ? rawFiles : [];

        if (!list.length) throw new Error("Nenhum arquivo BIM encontrado para este token.");
        if (cancelled) return;

        setAssets(list);
        const chosen = list[Math.max(0, Math.min(activeIndex, list.length - 1))];

        const ifcUrl = chosen.ifcPath
          ? getSharedProxyUrl({ baseUrl, token: shareToken, path: chosen.ifcPath })
          : chosen.ifcUrl;

        if (!ifcUrl) throw new Error("Arquivo IFC não encontrado neste projeto compartilhado.");

        // eslint-disable-next-line no-console
        console.log("[SharedBIM] chosen:", JSON.stringify(chosen));
        // eslint-disable-next-line no-console
        console.log("[SharedBIM] ifcUrl:", ifcUrl);

        const loader = new IFCLoader();
        const publicUrl = process.env.PUBLIC_URL || "";
        const wasmBase = window.location.origin + publicUrl;
        // eslint-disable-next-line no-console
        console.log("[SharedBIM] wasmBase:", wasmBase);

        const isCOI = typeof window !== "undefined" && window.crossOriginIsolated;
        const wasmFileName = isCOI ? "web-ifc-mt.wasm" : "web-ifc.wasm";
        const wasmFetchUrl = `${wasmBase}/${wasmFileName}`;

        let wasmBuf = null;
        try {
          const wasmResp = await fetch(wasmFetchUrl);
          wasmBuf = await wasmResp.arrayBuffer();
          const firstBytes = Array.from(new Uint8Array(wasmBuf, 0, 4))
            .map(b => b.toString(16).padStart(2, "0")).join(" ");
          // eslint-disable-next-line no-console
          console.log(`[SharedBIM] WASM status=${wasmResp.status} bytes[0..3]=${firstBytes} byteLength=${wasmBuf.byteLength}`);
        } catch (fetchErr) {
          // eslint-disable-next-line no-console
          console.error("[SharedBIM] Falha ao pré-buscar WASM:", fetchErr);
        }

        // eslint-disable-next-line no-console
        console.log("[SharedBIM] Chamando Init() com wasmBinary pré-carregado...");
        await loader.ifcManager.state.api.Init(
          (path) => {
            if (path.endsWith(".worker.js")) return `${wasmBase}/${path}`;
            return path;
          },
          wasmBuf ? { wasmBinary: wasmBuf } : {}
        );
        // eslint-disable-next-line no-console
        console.log("[SharedBIM] Init() concluído.");
        await loader.ifcManager.applyWebIfcConfig({
          COORDINATE_TO_ORIGIN: true,
          USE_FAST_BOOLEANS: false,
        });
        ifcLoaderRef.current = loader;

        const scene = sceneRef.current;
        if (!scene || cancelled) return;

        setLoadingMsg("Lendo arquivo IFC…");
        // eslint-disable-next-line no-console
        console.log("[SharedBIM] Iniciando loadAsync:", ifcUrl);
        const model   = await loader.loadAsync(ifcUrl);
        // eslint-disable-next-line no-console
        console.log("[SharedBIM] loadAsync concluído. modelID:", model?.modelID);
        if (cancelled) return;

        const modelID = model.modelID;
        ifcModelIdRef.current = modelID;
        modelRootRef.current  = model;

        model.visible = false;
        scene.add(model);

        const box    = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size   = box.getSize(new THREE.Vector3());
        model.position.sub(center);
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) {
          const scale = 20 / maxDim;
          if (scale < 1) model.scale.multiplyScalar(scale);
        }

        setLoadingMsg("Construindo camadas…");
        const builtLayers    = [];
        const subsetMeshes   = [];
        const categorizedIds = new Set();

        const buildSubset = (name, ids) => {
          const subset = loader.ifcManager.createSubset({
            modelID,
            ids,
            scene,
            removePrevious: true,
            material: new THREE.MeshLambertMaterial({
              color: 0xc5d0e0,
              side: THREE.DoubleSide,
            }),
          });
          if (subset) {
            subset.position.copy(model.position);
            subset.scale.copy(model.scale);
            builtLayers.push({ name, visible: true, subsetMesh: subset });
            subsetMeshes.push(subset);
          }
        };

        for (const cat of IFC_CATEGORIES) {
          let ids = [];
          for (const type of cat.types) {
            try {
              const typeIds = await loader.ifcManager.getAllItemsOfType(modelID, type, false);
              if (Array.isArray(typeIds)) ids = [...ids, ...typeIds];
            } catch {}
          }
          if (!ids.length) continue;
          ids.forEach(id => categorizedIds.add(id));
          buildSubset(cat.name, ids);
        }

        // Catch-all: captura elementos de tipos conhecidos que não foram categorizados
        {
          const othersIds = [];
          for (const type of IFC_CATCHALL_TYPES) {
            try {
              const typeIds = await loader.ifcManager.getAllItemsOfType(modelID, type, false);
              if (Array.isArray(typeIds)) {
                typeIds.forEach(id => { if (!categorizedIds.has(id)) othersIds.push(id); });
              }
            } catch {}
          }
          if (othersIds.length) buildSubset("Outros", othersIds);
        }

        if (!cancelled) {
          if (builtLayers.length > 0) {
            selectableMeshesRef.current = subsetMeshes;
            layerTogglesRef.current = builtLayers;
            setLayerToggles(builtLayers);
          } else {
            layerTogglesRef.current = [];
            model.visible = true;
            const meshes = [];
            model.traverse(o => { if (o.isMesh) meshes.push(o); });
            selectableMeshesRef.current = meshes;
          }

          frameCameraFit();
          syncBimHelpers();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[SharedBimViewer] erro:", err);
        if (!cancelled) setError("Falha ao carregar o BIM compartilhado.");
      } finally {
        if (!cancelled) { setLoading(false); setLoadingMsg("Carregando…"); }
      }
    };

    load();
    return () => { cancelled = true; };
    // frameCameraFit/syncBimHelpers são estáveis (useCallback); incluir no array recarregaria o IFC sem necessidade
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken, activeIndex]);

  useEffect(() => {
    if (loading || error) return;
    if (!modelRootRef.current) return;
    syncBimHelpers();
  }, [showGrid, showAxes, layerToggles, loading, error, syncBimHelpers]);

  useEffect(() => {
    const onKeyDown = e => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (e.key === "Escape") {
        e.preventDefault();
        setMeasureMode(null);
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        setHoverTip(null);
        clearHighlightAndSelection();
        return;
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        frameCameraFit();
        return;
      }
      if (e.key === "1") {
        e.preventDefault();
        setMeasureMode(null);
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
      if (e.key === "2") {
        e.preventDefault();
        setMeasureMode("distance");
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
      if (e.key === "3") {
        e.preventDefault();
        setMeasureMode("area");
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearMeasurements, clearHighlightAndSelection, frameCameraFit]);

  const onToggleLayer = useCallback(layerName => {
    setLayerToggles(prev =>
      prev.map(l => {
        if (l.name !== layerName) return l;
        if (l.subsetMesh) l.subsetMesh.visible = !l.visible;
        return { ...l, visible: !l.visible };
      })
    );
  }, []);

  const measurementLabel = useMemo(() => {
    if (measurementValue === null || measurementValue === undefined) return null;
    if (measureMode === "distance") return `Distância: ${measurementValue.toFixed(2)} m`;
    if (measureMode === "area")     return `Área: ${measurementValue.toFixed(2)} m²`;
    return null;
  }, [measurementValue, measureMode]);

  const measureHelpText = useMemo(() => {
    if (measureMode === "distance") {
      return "Dois cliques na superfície do modelo (não use arraste): primeiro extremo, depois o segundo. A linha tracejada amarela é só prévia até o próximo clique. Após ver o valor, clique de novo para medir outro trecho.";
    }
    if (measureMode === "area") {
      return "Vários cliques na superfície: cada clique é um vértice do polígono (mínimo 3). A tracejada mostra a prévia até o próximo clique. Gire a cena com o botão direito/drag nos controles de órbita.";
    }
    return "Escolha Distância ou Área nos botões abaixo. Atalhos: Esc limpar · R encaixar · 1 Selecionar · 2 Distância · 3 Área.";
  }, [measureMode]);

  return (
    <div className="bim-viewer-mount bim-viewer-mount--standalone">
    <div className="bim-viewer-root" ref={viewerRootRef}>
      <div className="QuitButtonTour">
        <button type="button" title="Fechar" aria-label="Fechar e voltar" onClick={handleCloseShared}>
          <CloseIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      <div className="bim-viewer__header">
        <div className="bim-viewer__title">
          Visualização compartilhada BIM
          {loading  && <span className="bim-viewer__status">{loadingMsg}</span>}
          {!loading && error && <span className="bim-viewer__status bim-viewer__status--error">Erro</span>}
        </div>
        <div className="bim-viewer__spacer" />
        {assets.length > 1 && (
          <select
            className="bim-viewer__select"
            value={activeIndex}
            onChange={e => setActiveIndex(Number(e.target.value))}
          >
            {assets.map((a, i) => (
              <option key={a.name || i} value={i}>{a.name || `Modelo ${i + 1}`}</option>
            ))}
          </select>
        )}
      </div>

      <div className="bim-viewer__canvas-wrap" ref={canvasMountRef} />

      {hoverTip && (
        <div
          className="bim-viewer__hover-tooltip"
          style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
        >
          {hoverTip.text}
        </div>
      )}

      <div className="bim-viewer__panel bim-viewer__panel--left">
        <div className="bim-viewer__panel-title"><LayersIcon fontSize="small" /> Camadas</div>
        {layerToggles.length === 0 ? (
          <div className="bim-viewer__panel-empty">{loading ? "Construindo…" : "Sem camadas"}</div>
        ) : (
          <div className="bim-viewer__layers">
            {layerToggles.map(l => (
              <label key={l.name} className="bim-viewer__layer-item">
                <input type="checkbox" checked={l.visible} onChange={() => onToggleLayer(l.name)} />
                <span className="bim-viewer__layer-name">{l.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="bim-viewer__panel bim-viewer__panel--right">
        <div className="bim-viewer__panel-title"><PlaceIcon fontSize="small" /> Propriedades</div>
        {selectedProps ? (
          <div className="bim-viewer__meta">
            <pre className="bim-viewer__meta-pre">{safeJsonStringify(selectedProps)}</pre>
          </div>
        ) : (
          <div className="bim-viewer__panel-empty">Clique em um elemento para ver as propriedades IFC.</div>
        )}

        <div className="bim-viewer__divider" />

        <div className="bim-viewer__panel-title"><StraightenIcon2 fontSize="small" /> Medição</div>
        <div className="bim-viewer__measure-info">
          <div className="bim-viewer__measure-mode">{measureHelpText}</div>
          {measurementLabel && <div className="bim-viewer__measure-value">{measurementLabel}</div>}
          {(measureMode === "distance" || measureMode === "area") && measurementPoints.length > 0 && (
            <div className="bim-viewer__measure-export">
              <button type="button" className="bim-viewer__export-btn" onClick={exportMeasurements}>
                <GetAppIcon />
                Exportar medições (JSON)
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bim-viewer__controls">
        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--left">
        <div className="bim-viewer__controls-group bim-viewer__controls-group--left">
          <Tooltip title="Selecionar elemento (propriedades IFC) · atalho 1">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${measureMode === null ? "active" : ""}`}
              onClick={() => {
                setMeasureMode(null);
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
            >
              Selecionar
            </button>
          </Tooltip>
          <Tooltip title="Medir distância · atalho 2">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${measureMode === "distance" ? "active" : ""}`}
              onClick={() => {
                setMeasureMode("distance");
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
              aria-label="Medir distância"
            >
              <StraightenIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title="Medir área (polígono) · atalho 3">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${measureMode === "area" ? "active" : ""}`}
              onClick={() => {
                setMeasureMode("area");
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
              aria-label="Medir área"
            >
              <SquareFootIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title="Limpar medições">
            <button
              type="button"
              className="bim-viewer__tool-btn bim-viewer__tool-btn--icon-only"
              onClick={() => { clearMeasurements(); setMeasurementPoints([]); setMeasurementValue(null); }}
              aria-label="Limpar medições"
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${isFullscreen ? "active" : ""}`}
              onClick={toggleFullscreen}
              aria-label="Tela cheia"
            >
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </button>
          </Tooltip>
        </div>
        </div>

        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--center">
        <div className="bim-viewer__controls-group bim-viewer__controls-group--center">
          <Tooltip title="Vista de cima (planta)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewTop}>
              Topo
            </button>
          </Tooltip>
          <Tooltip title="Vista isométrica">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewIso}>
              Iso
            </button>
          </Tooltip>
          <Tooltip title="Vista frontal (elevação)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewFront}>
              Frontal
            </button>
          </Tooltip>
          <Tooltip title="Vista lateral direita">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewRight}>
              Direita
            </button>
          </Tooltip>
          <Tooltip title="Encaixar modelo na tela (atalho R)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={frameCameraFit}>
              Encaixar
            </button>
          </Tooltip>
          <Tooltip title={showGrid ? "Ocultar grade" : "Mostrar grade"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${showGrid ? "active" : ""}`}
              onClick={() => setShowGrid(v => !v)}
            >
              Grade
            </button>
          </Tooltip>
          <Tooltip title={showAxes ? "Ocultar eixos XYZ" : "Mostrar eixos XYZ"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${showAxes ? "active" : ""}`}
              onClick={() => setShowAxes(v => !v)}
            >
              Eixos
            </button>
          </Tooltip>
          <span className="bim-viewer__shortcuts-hint" aria-hidden="true">
            Esc · R · 1–3
          </span>
        </div>
        </div>

        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--right" aria-hidden="true" />
      </div>

      {error && !loading && (
        <div className="bim-viewer__error"><p>{error}</p></div>
      )}

      <LoginPopup isPublic={true} />
    </div>
    </div>
  );
};

export default SharedBimViewer;
